import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import PipedriveDataMapping from "./PipedriveDataMapping";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { SectionCard } from "@/components/ds";
const PD_COLOR = "#172B4D";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingPipedrive() {
  const navigate = useNavigate();
  return (
    <button type="button" className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}>
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PipedriveConfig {
  id: number;
  siteId: number;
  displayName: string;
  connectedAs: string;
  companyDomain: string;
  apiTokenMasked?: string | null;
  authMethod?: string;
  oauthConnectedAt?: string | null;
  enabled: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "field-reference" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Contact & deal lookup",
    icon: "👤",
    desc: "Find anyone in Pipedrive by email, phone, or name instantly in chat"
  },
  {
    title: "Deal management",
    icon: "💼",
    desc: "Create, update, and close deals without leaving the conversation"
  },
  {
    title: "Activity logging",
    icon: "📝",
    desc: "Auto-log every chat as an activity on the right contact"
  },
  {
    title: "Pipeline visibility",
    icon: "📊",
    desc: "See deal stage, value, and close date during support conversations"
  },
  {
    title: "Organization intelligence",
    icon: "🏢",
    desc: "Company-level view: all contacts, deals, and history at a glance"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "pipedrive_get_contact", label: "Get contact", group: "Contacts", description: "Retrieve a contact by ID — name, email, phone, organization, and deal history." },
  { name: "pipedrive_search_contacts", label: "Search contacts", group: "Contacts", description: "Find contacts by email, phone, or name. Returns matching profiles with deal counts." },
  { name: "pipedrive_create_contact", label: "Create contact", group: "Contacts", description: "Add a new person to Pipedrive with name, email, phone, and organization link." },
  { name: "pipedrive_update_contact", label: "Update contact", group: "Contacts", description: "Update contact fields — email, phone, organization, custom fields." },

  { name: "pipedrive_get_deal", label: "Get deal", group: "Deals", description: "Full deal details: title, stage, value, owner, close date, and linked contacts." },
  { name: "pipedrive_search_deals", label: "Search deals", group: "Deals", description: "Find deals by title, contact, or organization. Returns stage and value." },
  { name: "pipedrive_create_deal", label: "Create deal", group: "Deals", description: "Create a new deal linked to a contact or organization with title, value, and stage." },
  { name: "pipedrive_update_deal", label: "Update deal", group: "Deals", description: "Update deal value, stage, close date, or custom fields." },
  { name: "pipedrive_close_deal", label: "Close deal", group: "Deals", description: "Mark a deal as Won or Lost with an optional reason." },
  { name: "pipedrive_get_deal_activities", label: "Get deal activities", group: "Deals", description: "All activities (calls, emails, tasks) logged against a deal." },

  { name: "pipedrive_get_organization", label: "Get organization", group: "Organizations", description: "Organization profile with address, contacts count, open deal count, and deal value." },
  { name: "pipedrive_search_organizations", label: "Search organizations", group: "Organizations", description: "Find organizations by name. Returns address, deal count, and linked contacts." },
  { name: "pipedrive_create_organization", label: "Create organization", group: "Organizations", description: "Add a new company to Pipedrive with name, address, and custom fields." },

  { name: "pipedrive_create_activity", label: "Create activity", group: "Activities", description: "Log a call, task, meeting, or email against a deal or contact." },
  { name: "pipedrive_get_activities", label: "Get activities", group: "Activities", description: "Retrieve upcoming or past activities for a contact or deal — type, due date, notes." },
  { name: "pipedrive_complete_activity", label: "Complete activity", group: "Activities", description: "Mark an activity as done with optional outcome note." },

  { name: "pipedrive_create_note", label: "Create note", group: "Notes", description: "Add an internal note to a deal, contact, or organization." },
  { name: "pipedrive_get_notes", label: "Get notes", group: "Notes", description: "Retrieve notes for a deal or contact — content, author, and created date." },

  { name: "pipedrive_get_pipelines", label: "Get pipelines", group: "Pipelines", description: "All sales pipelines with their names and stage counts." },
  { name: "pipedrive_get_stages", label: "Get stages", group: "Pipelines", description: "All stages in a pipeline with probability and order index." },
  { name: "pipedrive_move_deal_stage", label: "Move deal stage", group: "Pipelines", description: "Move a deal to a different pipeline stage by name or ID." },

  { name: "pipedrive_get_persons", label: "Get persons", group: "Persons", description: "List all persons in Pipedrive with pagination — name, email, and organization." },
  { name: "pipedrive_get_timeline", label: "Get timeline", group: "Persons", description: "Contact activity timeline — all deals, activities, and notes in chronological order." }
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const PD_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "id", label: "Contact ID", notes: "Pipedrive person ID" },
      { field: "name", label: "Full Name", notes: "First + last name" },
      { field: "email", label: "Email", notes: "Primary email address" },
      { field: "phone", label: "Phone", notes: "Primary phone number" },
      { field: "org_id", label: "Organization ID", notes: "Linked organization" },
      { field: "open_deals_count", label: "Open Deals", notes: "Count of active deals" },
      { field: "owner_id", label: "Owner", notes: "Assigned sales rep" }
    ]
  },
  {
    scope: "Deal",
    scopeKey: "deal",
    fields: [
      { field: "id", label: "Deal ID", notes: "Pipedrive deal ID" },
      { field: "title", label: "Deal Title", notes: "Name of the deal" },
      { field: "value", label: "Deal Value", notes: "Monetary value" },
      { field: "currency", label: "Currency", notes: "ISO currency code" },
      { field: "stage_id", label: "Stage", notes: "Current pipeline stage" },
      { field: "status", label: "Status", notes: "open / won / lost / deleted" },
      { field: "close_time", label: "Close Date", notes: "Expected or actual close date" },
      { field: "probability", label: "Win Probability", notes: "0-100 percentage" }
    ]
  },
  {
    scope: "Organization",
    scopeKey: "organization",
    fields: [
      { field: "id", label: "Org ID", notes: "Pipedrive organization ID" },
      { field: "name", label: "Company Name", notes: "Organization display name" },
      { field: "address", label: "Address", notes: "Full address string" },
      { field: "open_deals_count", label: "Open Deals", notes: "Active deal count" },
      { field: "people_count", label: "Contact Count", notes: "Linked contacts count" }
    ]
  }
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{children}</pre>;
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(`{${variable}}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ok */ }
  }
  return (
    <button type="button" onClick={copy} title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${copied ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}>
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

// ─── Chevron ──────────────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
    </svg>
  );
}

// ─── FindKeyAccordion ─────────────────────────────────────────────────────────

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden mt-2">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
        aria-expanded={open}>
        <span>Where to find your Personal API Token</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 text-sm text-slate-600 space-y-1.5 bg-white border-t border-slate-100">
          <ol className="list-none space-y-2">
            {[
              "Log in to Pipedrive at app.pipedrive.com",
              "Click your avatar (top-right) → Personal preferences",
              "Go to the API tab",
              "Copy your Personal API token"
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5" style={{ backgroundColor: PD_COLOR }}>
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ─── CapabilitiesPanel ────────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas · 24 AI tools</span>
        </div>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>All 24 AI tools activate automatically on connect</strong> — no extra configuration required.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{cap.icon}</span>
                  <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                </div>
                <p className="text-xs text-slate-500">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HowItWorksCard ───────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard title="How Pipedrive works in Velaro" description="Connect once — every contact, deal, and pipeline becomes AI-searchable instantly.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: PD_COLOR }}>1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect Pipedrive</span>
          </div>
          <p className="text-xs text-slate-500">Click "Connect Pipedrive" and authorize via OAuth — or use a legacy API token.</p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">24 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700">Contacts, deals, pipelines, activities, notes — all AI-searchable in real time.</p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-[var(--color-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">AI-powered CRM support</span>
          </div>
          <p className="text-xs text-blue-700">Agents see full deal context during every chat — no tab switching, no manual lookups.</p>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard
          title="Connect your Pipedrive account"
          description="Enter your API token and all your contacts, deals, and pipeline data become instantly searchable by the AI during any conversation."
          action={<button type="button" onClick={onConnect} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: PD_COLOR }}>Connect Now →</button>}
        />
      ) : (
        <SectionCard
          title="Launch Starter Workflow Wizard"
          description="Build a Pipedrive CRM bot in minutes using a pre-built template — deal lookup, lead capture, or activity logging."
          action={<button type="button" onClick={onOpenWizard} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: PD_COLOR }}>Launch Wizard →</button>}
        />
      )}
    </div>
  );
}

// ─── AiToolsTab ──────────────────────────────────────────────────────────────

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI Tools"
      description="All tools activate automatically on connect — no configuration required."
      action={hasConnection ? <LaunchWizardButton onClick={onOpenWizard} /> : <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500`}>No connection</span>}
    >
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: PD_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-slate-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 pt-8 border-t border-zinc-200">
        <SkillControlsPanel integration="pipedrive" skills={AI_TOOLS} />
      </div>
    </SectionCard>
  );
}

// ─── AccountsTab ──────────────────────────────────────────────────────────────

interface LegacyFormState {
  displayName: string;
  apiToken: string;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

const LEGACY_FORM_DEFAULT: LegacyFormState = {
  displayName: "", apiToken: "", saving: false, testing: false, error: null, testResult: null
};

function AccountsTab({
  configs,
  onRefresh,
  oauthError,
  oauthSuccess
}: {
  configs: PipedriveConfig[];
  onRefresh(): void;
  oauthError?: string | null;
  oauthSuccess?: boolean;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [oauthFlowError, setOauthFlowError] = useState<string | null>(oauthError ?? null);
  const [showLegacy, setShowLegacy] = useState(false);
  const [form, setForm] = useState<LegacyFormState>(LEGACY_FORM_DEFAULT);

  async function handleOAuthConnect() {
    setConnecting(true);
    setOauthFlowError(null);
    try {
      const resp = await api.admin.get("Pipedrive/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setOauthFlowError(body?.error ?? "Failed to start OAuth flow. Please try again.");
        return;
      }
      const data = await resp.json() as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setOauthFlowError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Remove this Pipedrive connection?")) return;
    setDeleting(id);
    try {
      await api.messaging.delete(`Pipedrive/Configs/${id}`);
      onRefresh();
    } catch (ex) {
      console.error("Pipedrive delete failed", ex);
    } finally {
      setDeleting(null);
    }
  }

  async function handleLegacySave() {
    if (!form.apiToken.trim()) { setForm((f) => ({ ...f, error: "API Token is required." })); return; }
    setForm((f) => ({ ...f, saving: true, error: null, testResult: null }));
    try {
      const resp = await api.messaging.post("Pipedrive/Configs", {
        displayName: form.displayName.trim() || "Pipedrive",
        apiToken: form.apiToken.trim()
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setForm((f) => ({ ...f, saving: false, error: (d as { error?: string }).error ?? "Failed to save." }));
        return;
      }
      setForm(LEGACY_FORM_DEFAULT);
      setShowLegacy(false);
      onRefresh();
    } catch (ex) {
      console.error("Pipedrive legacy save failed", ex);
      setForm((f) => ({ ...f, saving: false, error: "Network error — please try again." }));
    }
  }

  async function handleLegacyTest() {
    if (!form.apiToken.trim()) { setForm((f) => ({ ...f, error: "Enter an API Token first." })); return; }
    setForm((f) => ({ ...f, testing: true, testResult: null, error: null }));
    try {
      const resp = await api.messaging.post("Pipedrive/Test", { apiToken: form.apiToken.trim() });
      const d = await resp.json().catch(() => ({})) as { valid?: boolean; error?: string; accountName?: string };
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: {
          success: d.valid ?? false,
          message: d.valid ? `Connected${d.accountName ? ` — ${d.accountName}` : ""}` : (d.error ?? "Connection test failed.")
        }
      }));
    } catch (ex) {
      console.error("Pipedrive test failed", ex);
      setForm((f) => ({ ...f, testing: false, testResult: { success: false, message: "Network error during test." } }));
    }
  }

  return (
    <div className="space-y-4">

      {/* OAuth success banner */}
      {oauthSuccess && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-800 font-medium">
          Pipedrive connected successfully via OAuth.
        </div>
      )}

      {/* ── Primary: Connect with OAuth ── */}
      <SectionCard title="Connect with Pipedrive" description="Recommended — secure OAuth 2.0, tokens refresh automatically">
        <p className="text-sm text-zinc-500 mb-5">
          You'll be redirected to Pipedrive to authorize Velaro, then brought back automatically.
          No API token to copy or manage — access renews every hour in the background.
        </p>

        {oauthFlowError && (
          <div className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800 mb-4" role="alert">
            {oauthFlowError}
          </div>
        )}

        <div className="flex justify-end">
          {connecting ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Redirecting to Pipedrive…
            </div>
          ) : (
            <button type="button" onClick={handleOAuthConnect}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: PD_COLOR }}>
              Connect with Pipedrive →
            </button>
          )}
        </div>
      </SectionCard>

      {/* ── Connected accounts list ── */}
      {configs.length > 0 && (
        <SectionCard title="Connected Accounts">
          <div className="space-y-3">
            {configs.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0" style={{ backgroundColor: PD_COLOR }}>P</div>
                  <div>
                    <div className="font-medium text-slate-800">{c.displayName || c.connectedAs || "Pipedrive"}</div>
                    {c.authMethod === "oauth" ? (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">OAuth</span>
                        {c.oauthConnectedAt && (
                          <span className="text-xs text-slate-400">Connected {new Date(c.oauthConnectedAt).toLocaleDateString()}</span>
                        )}
                        {c.connectedAs && <span className="text-xs text-slate-400">{c.connectedAs}</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400 font-mono">{c.apiTokenMasked}</span>
                        <button type="button" onClick={handleOAuthConnect}
                          className="text-xs text-blue-500 hover:text-blue-700 underline"
                          title="Upgrade to OAuth for Marketplace compatibility">
                          Upgrade to OAuth →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {c.enabled ? "Active" : "Disabled"}
                  </span>
                  <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors">
                    {deleting === c.id ? "Removing..." : "Remove"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Legacy: Personal API Token (collapsed) ── */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
        <button type="button"
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-slate-400 hover:text-slate-500 transition"
          onClick={() => setShowLegacy((v) => !v)}>
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium bg-slate-200 text-slate-500 px-2 py-0.5 rounded">Legacy</span>
            Personal API Token
          </span>
          <svg className={`w-4 h-4 transition-transform ${showLegacy ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showLegacy && (
          <div className="px-5 pb-5 border-t border-slate-200">
            <p className="text-xs text-slate-400 mt-3 mb-4">
              Personal API tokens work but are not required for Marketplace listing.
              Use OAuth above unless you have a specific reason to use a token.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Name <span className="text-slate-400">(optional)</span></label>
                <input type="text" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="e.g. My Pipedrive"
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Personal API Token <span className="text-red-500">*</span></label>
                <input type="password" value={form.apiToken} onChange={(e) => setForm((f) => ({ ...f, apiToken: e.target.value }))}
                  placeholder="Your Pipedrive API token"
                  autoComplete="new-password"
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono" />
                <FindKeyAccordion />
              </div>

              {form.error && (
                <div className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800" role="alert">{form.error}</div>
              )}
              {form.testResult && (
                <div className={`rounded-lg p-3 text-sm ${form.testResult.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`} role="status">
                  {form.testResult.message}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button type="button" onClick={handleLegacyTest} disabled={form.testing || form.saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50">
                  {form.testing ? "Testing…" : "Test Connection"}
                </button>
                <button type="button" onClick={handleLegacySave} disabled={form.saving || form.testing}
                  className="px-4 py-1.5 rounded-lg text-xs font-medium text-slate-600 border border-slate-300 hover:bg-slate-100 transition-colors disabled:opacity-50">
                  {form.saving ? "Saving…" : "Save Token"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ─── FieldReferenceTab ────────────────────────────────────────────────────────

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Contact");
  const scopeData = PD_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? PD_FIELD_REFERENCE[0];
  return (
    <SectionCard title="Field Reference" description="Variable names for HTTP POST nodes to push Pipedrive data to your systems.">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "contact_name":  "{pipedrive.contact.name}",
  "contact_email": "{pipedrive.contact.email}",
  "deal_title":    "{pipedrive.deal.title}",
  "deal_value":    "{pipedrive.deal.value}",
  "deal_stage":    "{pipedrive.deal.stage_id}",
  "org_name":      "{pipedrive.organization.name}"
}`}</CodeBlock>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {PD_FIELD_REFERENCE.map((s) => (
          <button key={s.scope} type="button" onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            style={activeScope === s.scope ? { backgroundColor: PD_COLOR } : {}}>
            {s.scope}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: PD_COLOR }}>{scopeData.scope} fields</div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Field</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Label</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Variable</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-700">{f.field}</td>
                  <td className="px-3 py-2 text-slate-700">{f.label}</td>
                  <td className="px-3 py-2"><CopyVariableButton variable={`pipedrive.${scopeData.scopeKey}.${f.field}`} /></td>
                  <td className="px-3 py-2 text-slate-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Pipedrive() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<PipedriveConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const { teamId } = useSelectedTeam();

  // Parse OAuth callback params from URL
  const urlParams = new URLSearchParams(location.search);
  const oauthConnected = urlParams.get("connected") === "true";
  const oauthErrorCode = urlParams.get("error");
  const oauthErrorDetail = urlParams.get("detail");
  // Combine the error code with the server/provider detail so the actual
  // cause is shown to the user instead of a bare "server_error".
  const oauthError = oauthErrorCode
    ? (oauthErrorDetail ? `${oauthErrorCode} — ${oauthErrorDetail}` : oauthErrorCode)
    : null;

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Pipedrive/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("Pipedrive fetchConfigs failed", ex);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  // Switch to Accounts tab automatically after OAuth callback, then strip query string
  useEffect(() => {
    if (oauthConnected || oauthError) {
      setActiveTab("accounts");
      navigate(location.pathname, { replace: true });
    }
  }, [oauthConnected, oauthError, navigate, location.pathname]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "field-reference", label: "Field Reference" },
    { id: "data-mapping", label: "Data Mapping" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard integration="pipedrive" onClose={() => setShowWizard(false)} />
      )}
      <CapabilitiesPanel />
      <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
        {TABS.map((tab) => (
          <button key={tab.id} type="button"
            className={`py-2 mr-4 text-sm cursor-pointer whitespace-nowrap ${activeTab === tab.id ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500"}`}
            style={activeTab === tab.id ? { borderColor: PD_COLOR, color: PD_COLOR } : {}}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.id === "accounts" && configs.length > 0 && (
              <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
            )}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">Deploy the chat widget to your site</div>
              <div className="text-xs text-blue-600">Your Pipedrive integration is connected — grab your embed code from Deployments to go live.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</Link>
              <Link to="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition">Get Embed Code →</Link>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab hasConnection={hasActiveConfig}
              onConnect={() => setActiveTab("accounts")}
              onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "accounts" && (
            <AccountsTab
              configs={configs}
              onRefresh={fetchConfigs}
              oauthError={oauthError}
              oauthSuccess={oauthConnected}
            />
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "data-mapping" && (
            <SectionCard title="Data Mapping — Pipedrive">
              <PipedriveDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <SectionCard title="Activity Log">
              <IntegrationActivityLog integrationSlug="Pipedrive" />
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}
